import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Link2,
  Plus,
  Loader2,
  Check,
  Image as ImageIcon,
  AlertCircle,
  Trash2,
  Eye,
  Download
} from "lucide-react";
import api from "@/lib/api";

export function ImportScooterDialog({ onImportSuccess }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [mainUrl, setMainUrl] = useState("");
  const [additionalUrls, setAdditionalUrls] = useState([""]);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const addUrlField = () => {
    setAdditionalUrls([...additionalUrls, ""]);
  };

  const removeUrlField = (index) => {
    setAdditionalUrls(additionalUrls.filter((_, i) => i !== index));
  };

  const updateAdditionalUrl = (index, value) => {
    const updated = [...additionalUrls];
    updated[index] = value;
    setAdditionalUrls(updated);
  };

  const handlePreview = async () => {
    if (!mainUrl) {
      toast.error("Entrez une URL");
      return;
    }

    setPreviewing(true);
    setPreview(null);

    try {
      const response = await api.post("/scooters/preview-import", {
        url: mainUrl,
        additional_urls: additionalUrls.filter(u => u.trim())
      });
      setPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la prévisualisation");
    } finally {
      setPreviewing(false);
    }
  };

  const handleImport = async () => {
    if (!mainUrl) {
      toast.error("Entrez une URL");
      return;
    }

    setLoading(true);
    setImportResult(null);

    try {
      const response = await api.post("/scooters/import-from-url", {
        url: mainUrl,
        additional_urls: additionalUrls.filter(u => u.trim())
      });
      
      setImportResult(response.data);
      toast.success(`Scooter importé: ${response.data.scooter_id}`);
      
      if (onImportSuccess) {
        onImportSuccess(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'import");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setMainUrl("");
    setAdditionalUrls([""]);
    setPreview(null);
    setImportResult(null);
  };

  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="btn-import-scooter">
          <Link2 className="w-4 h-4" />
          Importer depuis URL
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" />
            Importer un Scooter depuis URL
          </DialogTitle>
          <CardDescription>
            Collez le lien d'un scooter pour importer automatiquement toutes ses données et photos
          </CardDescription>
        </DialogHeader>

        {importResult ? (
          // Success state
          <div className="space-y-4 py-4">
            <div className="flex items-center gap-3 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
              <Check className="w-8 h-8 text-green-500" />
              <div>
                <p className="font-medium text-green-500">Scooter importé avec succès!</p>
                <p className="text-sm text-muted-foreground">
                  ID: {importResult.scooter_id} • {importResult.photos_imported} photos importées
                </p>
              </div>
            </div>

            <div className="bg-muted/30 p-4 rounded-lg">
              <h4 className="font-medium mb-2">Détails importés:</h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>Marque: <span className="font-medium">{importResult.scooter?.brand}</span></div>
                <div>Modèle: <span className="font-medium">{importResult.scooter?.model}</span></div>
                <div>Année: <span className="font-medium">{importResult.scooter?.year}</span></div>
                <div>Kilométrage: <span className="font-medium">{importResult.scooter?.mileage} km</span></div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={resetForm} className="flex-1">
                Importer un autre
              </Button>
              <Button onClick={handleClose} className="flex-1 bg-primary text-black">
                Fermer
              </Button>
            </div>
          </div>
        ) : (
          // Input state
          <div className="space-y-4 py-4">
            {/* Main URL */}
            <div>
              <Label htmlFor="main-url">URL principale *</Label>
              <Input
                id="main-url"
                type="url"
                value={mainUrl}
                onChange={(e) => setMainUrl(e.target.value)}
                placeholder="https://example.com/stock/SC123ABC"
                className="mt-1"
                data-testid="input-import-url"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Collez le lien de la page du scooter
              </p>
            </div>

            {/* Additional URLs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>URLs additionnelles (optionnel)</Label>
                <Button type="button" variant="ghost" size="sm" onClick={addUrlField}>
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              </div>
              <div className="space-y-2">
                {additionalUrls.map((url, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      type="url"
                      value={url}
                      onChange={(e) => updateAdditionalUrl(index, e.target.value)}
                      placeholder="URL pour plus de photos..."
                    />
                    {additionalUrls.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeUrlField(index)}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Ajoutez des liens vers d'autres pages pour récupérer plus de photos
              </p>
            </div>

            {/* Preview */}
            {preview && (
              <Card className="border-primary/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Eye className="w-4 h-4" />
                    Aperçu des données
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{preview.source}</Badge>
                    <span className="text-sm text-muted-foreground">{preview.base_url}</span>
                  </div>
                  
                  {preview.data && Object.keys(preview.data).length > 0 && (
                    <div className="bg-muted/30 p-3 rounded text-sm">
                      {preview.data.brand && <div>Marque: <strong>{preview.data.brand}</strong></div>}
                      {preview.data.model && <div>Modèle: <strong>{preview.data.model}</strong></div>}
                      {preview.data.year && <div>Année: <strong>{preview.data.year}</strong></div>}
                      {preview.data.mileage && <div>KM: <strong>{preview.data.mileage}</strong></div>}
                      {preview.data.selling_price && <div>Prix: <strong>{preview.data.selling_price}€</strong></div>}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-2 text-sm">
                    <ImageIcon className="w-4 h-4 text-muted-foreground" />
                    <span>{preview.photos_found} photos trouvées</span>
                  </div>
                  
                  {preview.photo_urls?.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {preview.photo_urls.slice(0, 4).map((url, idx) => (
                        <img
                          key={idx}
                          src={url.startsWith('http') ? url : `${preview.base_url}${url}`}
                          alt={`Preview ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      ))}
                      {preview.photo_urls.length > 4 && (
                        <div className="w-16 h-16 bg-muted rounded flex items-center justify-center text-sm text-muted-foreground">
                          +{preview.photo_urls.length - 4}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handlePreview}
                disabled={!mainUrl || previewing}
                className="flex-1"
              >
                {previewing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Prévisualiser
              </Button>
              <Button
                onClick={handleImport}
                disabled={!mainUrl || loading}
                className="flex-1 bg-primary text-black"
                data-testid="btn-confirm-import"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Importer
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ImportScooterDialog;
